import { c as _c } from "react/compiler-runtime";
import { getCoreUsageWindow, useCoreUsageTimeseriesNonSuspense, useCurrentCoreUsageNonSuspense } from "@/hooks/useUsage";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { Area, AreaChart, CartesianGrid, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { BlacksmithLogomarkAnimationSVG } from "@/components/BlacksmithLogoSVG";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState, useMemo, useRef, useEffect, ReactElement } from "react";
import { Cpu, Activity, ChevronRight, Info, X } from "lucide-react";
import { motion } from "motion/react";
import { useLiveMetricsSheetState } from "@/hooks/useLiveMetricsSheetState";
const chartConfig = {
  "total.vcpus": {
    label: "vCPUs",
    color: "var(--chart-1)"
  },
  "total.jobs": {
    label: "Active Jobs",
    color: "var(--chart-2)"
  }
} satisfies ChartConfig;
const ChartSkeleton = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-[232px] w-full min-w-0 flex items-center justify-center"><div className="w-20 h-20"><BlacksmithLogomarkAnimationSVG /></div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const NoDataState = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-[232px] border border-dashed rounded-lg flex items-center justify-center text-neutral-400 dark:text-neutral-600 text-sm">No data</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
interface StatCardProps {
  current: number | null;
  peak: number | null;
  label: string;
  icon: ReactElement;
  isCurrentLoading: boolean;
  isPeakLoading: boolean;
  explanation?: string;
}
const StatCard = t0 => {
  const $ = _c(20);
  const {
    current,
    peak,
    label,
    icon,
    isCurrentLoading,
    isPeakLoading,
    explanation
  } = t0;
  let t1;
  if ($[0] !== isPeakLoading || $[1] !== peak) {
    t1 = isPeakLoading ? "\u2013" : peak !== null ? peak.toLocaleString("en-US") : "\u2013";
    $[0] = isPeakLoading;
    $[1] = peak;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <Badge variant="outline" className="absolute top-3 right-3 bg-neutral-50 dark:bg-transparent">Peak{" "}{t1}</Badge>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== current || $[6] !== isCurrentLoading) {
    t3 = isCurrentLoading ? <Skeleton className="h-12 w-12 rounded-sm" /> : current !== null ? current.toLocaleString("en-US") : "\u2013";
    $[5] = current;
    $[6] = isCurrentLoading;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <div className="flex items-baseline justify-center gap-2"><span className="type-numerals-pixel-l">{t3}</span></div>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== explanation) {
    t5 = explanation && <TooltipProvider><Tooltip><TooltipTrigger asChild={true}><button type="button" className="inline-flex cursor-pointer text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-border)] focus-visible:ring-offset-1 rounded" aria-label="More info"><Info className="h-3 w-3" /></button></TooltipTrigger><TooltipContent side="top" className="max-w-[280px] z-[100]"><p>{explanation}</p></TooltipContent></Tooltip></TooltipProvider>;
    $[10] = explanation;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== icon || $[13] !== label || $[14] !== t5) {
    t6 = <h4 className="type-uppercase-title text-neutral-600 dark:text-neutral-400 flex items-center justify-center gap-1.5">{icon}{label}{t5}</h4>;
    $[12] = icon;
    $[13] = label;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t2 || $[17] !== t4 || $[18] !== t6) {
    t7 = <div className="relative rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4 flex flex-col items-center justify-center gap-1">{t2}{t4}{t6}</div>;
    $[16] = t2;
    $[17] = t4;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
};
interface LiveMetricsIndicatorProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideIndicator?: boolean;
}
const LiveMetricsIndicator = ({
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  hideIndicator = false
}: LiveMetricsIndicatorProps) => {
  const {
    isOpen: hookIsOpen,
    setIsOpen: hookSetIsOpen
  } = useLiveMetricsSheetState();

  // Use external props if provided, otherwise use hook state
  const isSheetOpen = externalOpen !== undefined ? externalOpen : hookIsOpen;
  const setIsSheetOpen = (open: boolean) => {
    if (externalOnOpenChange) {
      externalOnOpenChange(open);
    } else {
      hookSetIsOpen(open);
    }
  };
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUserNonSuspense();
  const [timeRange, setTimeRange] = useState<"24h" | "7d">("24h");
  // Track the (org, timeRange) pair whose data has settled so we can detect in-flight switches.
  const committedParamsRef = useRef<{
    org: string | null | undefined;
    range: "24h" | "7d";
  }>({
    org: undefined,
    range: "24h"
  });
  const [titleTooltipOpen, setTitleTooltipOpen] = useState(false);
  const sheetJustOpenedRef = useRef(false);
  useEffect(() => {
    if (isSheetOpen) {
      setTitleTooltipOpen(false);
      sheetJustOpenedRef.current = true;
      const id = setTimeout(() => {
        sheetJustOpenedRef.current = false;
      }, 400);
      return () => clearTimeout(id);
    }
  }, [isSheetOpen]);
  const {
    currentUsage,
    isLoading: isCurrentLoading
  } = useCurrentCoreUsageNonSuspense();
  const hasCurrentUsage = currentUsage !== undefined;

  // Calculate window size and date range based on selected timeRange
  const {
    startDate,
    endDate
  } = useMemo(() => getCoreUsageWindow(15, timeRange === "24h" ? 24 : 7 * 24), [timeRange]);
  const {
    usageSnapshots,
    isLoading: isTimeseriesLoading,
    isFetching
  } = useCoreUsageTimeseriesNonSuspense(15, startDate, endDate);

  // True when an org or time range switch is still fetching (stale data from prior params is in usageSnapshots).
  const isParamSwitching = isFetching && (committedParamsRef.current.org !== activeGithubOrg || committedParamsRef.current.range !== timeRange);

  // Update the committed params ref once the fetch for the new params resolves.
  if (!isFetching) {
    committedParamsRef.current = {
      org: activeGithubOrg,
      range: timeRange
    };
  }

  // Determine if we should show loading state (initial load, org switch, or time range change)
  const showLoading = isTimeseriesLoading || isParamSwitching;

  // Peak is loading during initial load, org switches, or range switches; background 60s refetches
  // retain valid peak data via keepPreviousData, so isFetching alone does not trigger this.
  const isPeakLoading = isTimeseriesLoading || isParamSwitching;

  // Calculate peak values from the current time series data
  const {
    peakVcpus,
    peakJobs
  } = useMemo<{
    peakVcpus: number | null;
    peakJobs: number | null;
  }>(() => {
    if (!usageSnapshots?.length) return {
      peakVcpus: null,
      peakJobs: null
    };
    return {
      peakVcpus: Math.max(...usageSnapshots.map(s => Number.isFinite(s.total?.vcpus) ? s.total.vcpus : 0)),
      peakJobs: Math.max(...usageSnapshots.map(s_0 => Number.isFinite(s_0.total?.jobs) ? s_0.total.jobs : 0))
    };
  }, [usageSnapshots]);

  // Y-domain and ticks for vCPU chart
  const vcpuChartProps = useMemo(() => {
    const values = usageSnapshots?.map(s_1 => Number.isFinite(s_1.total?.vcpus) ? s_1.total.vcpus : 0) ?? [];
    const max = values.length ? Math.max(...values) : 1;
    const domainMax = max > 0 ? max : 1;
    const domain: [number, number] = [0, domainMax];
    const ticks: number[] = [];
    const count = 5;
    for (let i = 0; i < count; i++) {
      ticks.push(domainMax * i / (count - 1));
    }
    const formatY = (v: number) => {
      if (v === domain[0] || v === domain[1]) return "";
      return v >= 1 ? v.toFixed(0) : v.toFixed(1);
    };
    return {
      yDomain: domain,
      yTickValues: ticks,
      formatY
    };
  }, [usageSnapshots]);

  // Y-domain and ticks for jobs chart
  const jobsChartProps = useMemo(() => {
    const values_0 = usageSnapshots?.map(s_2 => Number.isFinite(s_2.total?.jobs) ? s_2.total.jobs : 0) ?? [];
    const max_0 = values_0.length ? Math.max(...values_0) : 1;
    const domainMax_0 = max_0 > 0 ? max_0 : 1;
    const domain_0: [number, number] = [0, domainMax_0];
    const ticks_0: number[] = [];
    const count_0 = 5;
    for (let i_0 = 0; i_0 < count_0; i_0++) {
      ticks_0.push(domainMax_0 * i_0 / (count_0 - 1));
    }
    const formatY_0 = (v_0: number) => {
      if (v_0 === domain_0[0] || v_0 === domain_0[1]) return "";
      return v_0.toFixed(0);
    };
    return {
      yDomain: domain_0,
      yTickValues: ticks_0,
      formatY: formatY_0
    };
  }, [usageSnapshots]);
  const xAxisInterval = useMemo(() => {
    const maxTicks = 8;
    const len = usageSnapshots?.length ?? 0;
    if (len <= maxTicks) return 0;
    return Math.ceil((len - 1) / maxTicks);
  }, [usageSnapshots?.length]);
  const formatTimestamp = (ts: string) => {
    const d = new Date(ts);
    return `${d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: "UTC"
    })} ${d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC"
    })} UTC`;
  };
  return <>
      {!hideIndicator && <div className="group/live-metrics flex flex-col border-b w-full h-16 items-center hover:cursor-pointer hover:bg-sidebar-accent transition-colors duration-75" onClick={() => setIsSheetOpen(true)}>
          <div className="flex gap-1 w-full items-center justify-between px-2 pr-1 mt-1.5">
            <span className="text-[0.65rem] text-neutral-600 dark:text-neutral-400 font-bold">
              vCPU Usage
            </span>
            <motion.div className="relative">
              <motion.div className="flex gap-1 text-[0.65rem] bg-sidebar-accent rounded-md pl-1.5 p-1 font-medium text-neutral-600 dark:text-neutral-400 translate-x-0 opacity-100 group-hover/live-metrics:opacity-0 group-hover/live-metrics:translate-x-3 transition-all duration-25">
                {isCurrentLoading ? "-" : hasCurrentUsage ? ((currentUsage.amd64.vcpus ?? 0) + (currentUsage.arm64.vcpus ?? 0) + (currentUsage.macos.vcpus ?? 0)).toLocaleString("en-US") : "\u2013"}
                <Cpu className="w-4 h-4 text-neutral-600 dark:text-neutral-400" />
              </motion.div>
              <motion.div className="flex gap-1 text-[0.65rem] bg-sidebar-accent rounded-md font-medium px-2 pr-1 py-1 text-neutral-600 dark:text-neutral-400 absolute top-0 right-0 opacity-0 translate-x-3 group-hover/live-metrics:opacity-100 group-hover/live-metrics:translate-x-0 transition-all duration-25 whitespace-nowrap">
                Show more
                <ChevronRight className="w-3.5 h-3.5" />
              </motion.div>
            </motion.div>
          </div>
          <div className="flex-1 w-full">
            <ResponsiveContainer width="100%" height="100%" debounce={100}>
              <AreaChart data={usageSnapshots} margin={{
            top: 10,
            right: 0,
            bottom: 0,
            left: 0
          }}>
                <Area dataKey="total.vcpus" stroke="var(--chart-1)" fill="var(--chart-1)" fillOpacity={0.2} strokeWidth={timeRange === "7d" ? 1 : 1.5} dot={false} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>}

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent hideCloseButton className="min-w-[720px] sm:min-w-[800px] max-w-[95vw] flex flex-col gap-y-6">
          <SheetHeader className="shrink-0 space-y-0">
            <div className="flex flex-col gap-0">
              <div className="flex justify-between items-center gap-4">
                <div className="flex items-baseline gap-1.5">
                  <SheetTitle className="-translate-y-0.5 type-page-title-l m-0">
                    Usage over time
                  </SheetTitle>
                  <TooltipProvider>
                    <Tooltip open={titleTooltipOpen} onOpenChange={open_0 => {
                    if (open_0 && sheetJustOpenedRef.current) return;
                    setTitleTooltipOpen(open_0);
                  }}>
                      <TooltipTrigger asChild>
                        <button type="button" className="inline-flex h-[1em] min-w-[1em] cursor-pointer items-center justify-center p-0 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-border)] focus-visible:ring-offset-1 rounded [&_svg]: shrink-0" aria-label="More info">
                          <Info className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[280px] z-[100]">
                        <p>
                          vCPU and active job usage over the selected time
                          range.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center gap-4">
                  <Select value={timeRange} onValueChange={(value: "24h" | "7d") => setTimeRange(value)}>
                    <SelectTrigger className="h-7 w-[180px]">
                      <SelectValue placeholder="Select time range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="24h">Last 24 hours</SelectItem>
                      <SelectItem value="7d">Last week</SelectItem>
                    </SelectContent>
                  </Select>
                  <SheetClose className="rounded-md transition-opacity hover:opacity-90 focus-visible:outline-none border border-transparent focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0 h-7 w-7 flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed shrink-0">
                    <X className="h-4 w-4 opacity-70" />
                    <span className="sr-only">Close</span>
                  </SheetClose>
                </div>
              </div>
              <div className="-mx-6 mt-6 mb-0 border-b border-[var(--color-border)]" />
            </div>
          </SheetHeader>
          <div className="flex flex-col gap-6 flex-1 min-h-0 overflow-y-auto pb-6">
            <div className="grid grid-cols-2 gap-6">
              <StatCard current={hasCurrentUsage ? (currentUsage.amd64.vcpus ?? 0) + (currentUsage.arm64.vcpus ?? 0) + (currentUsage.macos.vcpus ?? 0) : null} peak={peakVcpus} label="Current vCPU Usage" icon={<Cpu className="w-4 h-4 text-neutral-600 dark:text-neutral-400" />} isCurrentLoading={isCurrentLoading} isPeakLoading={isPeakLoading} explanation="Number of vCPUs currently in use across all running GitHub Actions jobs in your organization." />
              <StatCard current={hasCurrentUsage ? (currentUsage.amd64.jobs ?? 0) + (currentUsage.arm64.jobs ?? 0) + (currentUsage.macos.jobs ?? 0) : null} peak={peakJobs} label="Active Jobs" icon={<Activity className="w-4 h-4 text-neutral-600 dark:text-neutral-400" />} isCurrentLoading={isCurrentLoading} isPeakLoading={isPeakLoading} explanation="Number of GitHub Actions jobs currently running on Blacksmith runners." />
            </div>

            <div className="w-full border rounded-xl overflow-hidden bg-[var(--color-surface)]">
              <div className="type-uppercase-title opacity-50 flex items-center justify-between px-6 py-3">
                <span className="flex items-center gap-1.5">
                  vCPU Usage
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" className="inline-flex cursor-pointer text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-border)] focus-visible:ring-offset-1 rounded" aria-label="More info">
                          <Info className="h-3 w-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[280px] z-[100]">
                        <p>vCPUs in use over the selected time range.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </span>
              </div>
              {showLoading ? <ChartSkeleton /> : usageSnapshots.length === 0 ? <NoDataState /> : <ChartContainer config={chartConfig} className="h-[232px] w-full [&_.recharts-cartesian-axis-tick:first-child_.recharts-cartesian-axis-tick-line]:hidden [&_.recharts-cartesian-axis-tick:last-child_.recharts-cartesian-axis-tick-line]:hidden">
                  <AreaChart data={usageSnapshots} margin={{
                top: 0,
                right: 16,
                bottom: 8,
                left: 16
              }} syncId="metrics">
                    <defs>
                      <linearGradient id="fillVcpus" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--chart-1)" style={{
                      stopOpacity: "var(--chart-area-gradient-opacity)"
                    }} />
                        <stop offset="90%" stopColor="var(--chart-1)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="timestamp" interval={xAxisInterval} tick={{
                  fontSize: 10,
                  fill: "var(--chart-tick-label-color)"
                }} tickFormatter={v_1 => v_1 ? new Date(v_1).toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  timeZone: "UTC"
                }) : ""} axisLine={{
                  stroke: "var(--chart-axis-line-color)"
                }} tickLine={false} />
                    <YAxis domain={vcpuChartProps.yDomain} width={24} ticks={vcpuChartProps.yTickValues} tickFormatter={vcpuChartProps.formatY} tick={{
                  fontSize: 10,
                  fill: "var(--chart-tick-label-color)"
                }} axisLine={{
                  stroke: "var(--chart-axis-line-color)"
                }} tickLine={{
                  stroke: "var(--chart-axis-line-color)"
                }} />
                    <CartesianGrid vertical={false} horizontalValues={vcpuChartProps.yTickValues} stroke="var(--chart-dotted-grid-line)" strokeDasharray="2 1" opacity={0.15} />
                    <ReferenceLine y={0} stroke="var(--chart-axis-line-color)" strokeWidth={1} />
                    <ChartTooltip cursor={{
                  stroke: "var(--chart-axis-line-color)",
                  strokeWidth: 1
                }} content={<ChartTooltipContent indicator="dot" labelFormatter={(_, items) => items[0]?.payload?.timestamp ? formatTimestamp(items[0].payload.timestamp) : ""} />} />
                    <Area dataKey="total.vcpus" stroke="var(--chart-1)" fill="url(#fillVcpus)" fillOpacity={1} strokeWidth={timeRange === "7d" ? 1 : 1.5} dot={false} isAnimationActive={false} />
                  </AreaChart>
                </ChartContainer>}
            </div>

            <div className="w-full border rounded-xl overflow-hidden bg-[var(--color-surface)]">
              <div className="type-uppercase-title opacity-50 flex items-center justify-between px-6 py-3">
                <span className="flex items-center gap-1.5">
                  Active Jobs
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" className="inline-flex cursor-pointer text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-border)] focus-visible:ring-offset-1 rounded" aria-label="More info">
                          <Info className="h-3 w-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[280px] z-[100]">
                        <p>Running jobs over the selected time range.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </span>
              </div>
              {showLoading ? <ChartSkeleton /> : usageSnapshots.length === 0 ? <NoDataState /> : <ChartContainer config={chartConfig} className="h-[232px] w-full [&_.recharts-cartesian-axis-tick:first-child_.recharts-cartesian-axis-tick-line]:hidden [&_.recharts-cartesian-axis-tick:last-child_.recharts-cartesian-axis-tick-line]:hidden">
                  <AreaChart data={usageSnapshots} margin={{
                top: 0,
                right: 16,
                bottom: 8,
                left: 16
              }} syncId="metrics">
                    <defs>
                      <linearGradient id="fillJobs" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--chart-2)" style={{
                      stopOpacity: "var(--chart-area-gradient-opacity)"
                    }} />
                        <stop offset="90%" stopColor="var(--chart-2)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="timestamp" interval={xAxisInterval} tick={{
                  fontSize: 10,
                  fill: "var(--chart-tick-label-color)"
                }} tickFormatter={v_2 => v_2 ? new Date(v_2).toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  timeZone: "UTC"
                }) : ""} axisLine={{
                  stroke: "var(--chart-axis-line-color)"
                }} tickLine={false} />
                    <YAxis domain={jobsChartProps.yDomain} width={24} ticks={jobsChartProps.yTickValues} tickFormatter={jobsChartProps.formatY} tick={{
                  fontSize: 10,
                  fill: "var(--chart-tick-label-color)"
                }} axisLine={{
                  stroke: "var(--chart-axis-line-color)"
                }} tickLine={{
                  stroke: "var(--chart-axis-line-color)"
                }} />
                    <CartesianGrid vertical={false} horizontalValues={jobsChartProps.yTickValues} stroke="var(--chart-dotted-grid-line)" strokeDasharray="2 1" opacity={0.15} />
                    <ReferenceLine y={0} stroke="var(--chart-axis-line-color)" strokeWidth={1} />
                    <ChartTooltip cursor={{
                  stroke: "var(--chart-axis-line-color)",
                  strokeWidth: 1
                }} content={<ChartTooltipContent indicator="dot" labelFormatter={(__0, items_0) => items_0[0]?.payload?.timestamp ? formatTimestamp(items_0[0].payload.timestamp) : ""} />} />
                    <Area dataKey="total.jobs" stroke="var(--chart-2)" fill="url(#fillJobs)" fillOpacity={1} strokeWidth={timeRange === "7d" ? 1 : 1.5} dot={false} isAnimationActive={false} />
                  </AreaChart>
                </ChartContainer>}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>;
};
export default LiveMetricsIndicator;